import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { heroSlides, services, testimonials, homePageCopy } from '../data/mock';
import { SEO, pageSEO } from '../components/SEO';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8 }
};

export const HomePage = () => {
  const scrollToPhilosophy = () => {
    document.getElementById('philosophy')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-[var(--color-charcoal)]">
      <SEO {...pageSEO.home} path="/" />
      {/* Hero Section with Brand-Focused Slider */}
      <section className="relative h-screen min-h-[700px]">
        <Swiper
          modules={[Autoplay, EffectFade, Pagination]}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          autoplay={{
            delay: 7000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          pagination={{
            clickable: true,
            dynamicBullets: true,
          }}
          loop={true}
          speed={1500}
          className="h-full"
        >
          {heroSlides.map((slide) => (
            <SwiperSlide key={slide.id}>
              <div className="relative h-full w-full">
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img
                    src={slide.image}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[var(--color-charcoal)] via-[var(--color-charcoal)]/85 to-[var(--color-charcoal)]/40" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-charcoal)] via-transparent to-[var(--color-charcoal)]/30" />
                </div>
                
                {/* Content */}
                <div className="relative h-full flex items-center">
                  <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
                    {slide.type === 'brand' ? (
                      /* Brand Slide - Prominent Display */
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.5 }}
                        className="max-w-4xl"
                      >
                        <motion.h1 
                          initial={{ opacity: 0, y: 50 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 1.2, delay: 0.3 }}
                          className="font-serif text-6xl md:text-8xl lg:text-9xl mb-6 leading-[0.9] tracking-tight"
                        >
                          <span className="text-[var(--color-cream)]">The</span>
                          <br />
                          <span className="text-[var(--color-gold)]">Soul Script</span>
                        </motion.h1>
                        <motion.div 
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 1, delay: 0.8 }}
                          className="mt-8"
                        >
                          <p className="text-[var(--color-gold)] text-lg md:text-xl tracking-[0.2em] uppercase mb-4">
                            {slide.subtitle}
                          </p>
                          <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 max-w-2xl leading-relaxed">
                            {slide.description}
                          </p>
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 1, delay: 1.3 }}
                          className="mt-12"
                        >
                          <button
                            onClick={scrollToPhilosophy}
                            className="inline-flex items-center gap-3 px-8 py-4 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-semibold rounded-full hover:bg-[var(--color-gold-light)] transition-all duration-300 group"
                          >
                            {slide.cta.text}
                            <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
                          </button>
                        </motion.div>
                      </motion.div>
                    ) : (
                      /* Service Slides */
                      <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.3 }}
                        className="max-w-2xl"
                      >
                        <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
                          {slide.subtitle}
                        </span>
                        <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl mb-6 leading-[0.95]">
                          {slide.title}
                        </h2>
                        <p className="text-lg md:text-xl text-[var(--color-cream)]/70 mb-10 leading-relaxed max-w-lg">
                          {slide.description}
                        </p>
                        <Link
                          to={slide.cta.link}
                          className="inline-flex items-center gap-3 px-8 py-4 border-2 border-[var(--color-gold)] text-[var(--color-gold)] font-medium rounded-full hover:bg-[var(--color-gold)] hover:text-[var(--color-charcoal)] transition-all duration-300 group"
                        >
                          {slide.cta.text}
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-[var(--color-cream)]/30 rounded-full flex justify-center pt-2"
          >
            <div className="w-1 h-2 bg-[var(--color-gold)] rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section - Front and Center */}
      <section id="philosophy" className="py-24 lg:py-32 bg-[var(--color-charcoal)]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Opening Statement */}
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-12 leading-tight text-[var(--color-cream)]">
              {homePageCopy.opening}
            </h2>
          </motion.div>

          {/* Full Philosophy Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            {homePageCopy.philosophy.split('\n\n').map((paragraph, index) => (
              <p 
                key={index}
                className="text-lg md:text-xl text-[var(--color-cream)]/75 leading-[1.9] text-center"
              >
                {paragraph}
              </p>
            ))}
          </motion.div>

          {/* Closing Statement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-16 pt-12 border-t border-[var(--color-dark-purple)]/30"
          >
            <div className="text-center">
              {homePageCopy.closing.split('\n').map((line, index) => (
                <p 
                  key={index}
                  className={`text-xl md:text-2xl leading-relaxed ${
                    index === 0 
                      ? 'text-[var(--color-cream)]/60 mb-2' 
                      : 'text-[var(--color-gold)] font-serif'
                  }`}
                >
                  {line}
                </p>
              ))}
            </div>
          </motion.div>

          {/* CTA to explore more */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-12 text-center"
          >
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-[var(--color-gold)] hover:text-[var(--color-gold-light)] transition-colors group"
            >
              Read more about the philosophy
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Three Paths Section */}
      <section className="py-24 lg:py-32 bg-[var(--color-charcoal-light)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              Your Path
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl">
              Three Ways to Begin
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <Link
                  to={service.link}
                  className="group block h-full"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[4/5] mb-6">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className={`absolute inset-0 opacity-60 ${
                      service.color === 'bottle-green' 
                        ? 'bg-gradient-to-t from-[var(--color-bottle-green)] via-transparent to-transparent'
                        : service.color === 'deep-red'
                        ? 'bg-gradient-to-t from-[var(--color-deep-red)] via-transparent to-transparent'
                        : 'bg-gradient-to-t from-[var(--color-midnight-blue)] via-transparent to-transparent'
                    }`} />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="text-[var(--color-gold)] text-xs tracking-[0.2em] uppercase">
                        {service.subtitle}
                      </span>
                      <h3 className="font-serif text-2xl mt-2 group-hover:text-[var(--color-gold)] transition-colors">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-[var(--color-cream)]/60 leading-relaxed">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center gap-2 mt-4 text-[var(--color-gold)] text-sm group-hover:gap-3 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 lg:py-32 bg-[var(--color-dark-purple)]/15">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              Voices
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl">
              From Those Who've Walked This Path
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[var(--color-charcoal)] p-8 rounded-lg border border-[var(--color-dark-purple)]/30"
              >
                <p className="text-[var(--color-cream)]/80 mb-6 leading-relaxed italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-serif text-lg text-[var(--color-gold)]">
                    {testimonial.author}
                  </p>
                  <p className="text-sm text-[var(--color-cream)]/50">
                    {testimonial.context}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1618285545010-a44e4ffcd45b?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--color-charcoal)] via-[var(--color-charcoal)]/95 to-[var(--color-charcoal)]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-8 leading-tight">
              Ready to begin your journey of inquiry?
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/coaching"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[var(--color-bottle-green)] text-[var(--color-cream)] font-medium rounded-full hover:bg-[var(--color-bottle-green-light)] transition-all duration-300"
              >
                Begin Coaching
              </Link>
              <Link
                to="/tarot"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[var(--color-deep-red)] text-[var(--color-cream)] font-medium rounded-full hover:bg-[var(--color-deep-red-light)] transition-all duration-300"
              >
                Book a Reading
              </Link>
              <Link
                to="/collective"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-[var(--color-gold)] text-[var(--color-gold)] rounded-full hover:bg-[var(--color-gold)] hover:text-[var(--color-charcoal)] transition-all duration-300"
              >
                Read the Collective
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
